import React, { useState } from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, Zap, BatteryCharging, Car, Lightbulb, Wrench, Home as HomeIcon, Clock, Shield, Award, ChevronRight, Star, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { businessInfo, services, testimonials, reviewPlatforms } from '../mock';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  'battery-charging': BatteryCharging,
  'car': Car,
  'zap': Zap,
  'lightbulb': Lightbulb,
  'wrench': Wrench,
  'home': HomeIcon
};

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast({
        title: "Message Sent!",
        description: response.data.message,
      });
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try calling us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black/95 backdrop-blur-sm z-50 border-b border-yellow-400/20">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src="/agk-logo.jpg" alt="AGK Electric Inc. Logo" className="h-12 w-12 object-contain" />
              <div>
                <h1 className="text-xl font-bold text-white">{businessInfo.name}</h1>
                <p className="text-xs text-yellow-400">{businessInfo.certification}</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <a href="#services" className="text-gray-300 hover:text-yellow-400 transition-colors">Services</a>
              <a href="#about" className="text-gray-300 hover:text-yellow-400 transition-colors">About</a>
              <a href="#testimonials" className="text-gray-300 hover:text-yellow-400 transition-colors">Reviews</a>
              <a href="#contact" className="text-gray-300 hover:text-yellow-400 transition-colors">Contact</a>
              <a href={`tel:${businessInfo.phone}`} className="bg-yellow-400 hover:bg-yellow-500 text-black px-4 py-2 rounded-md font-semibold transition-colors">
                Call Now
              </a>
            </nav>
            <a href={`tel:${businessInfo.phone}`} className="md:hidden bg-yellow-400 hover:bg-yellow-500 text-black px-4 py-2 rounded-md font-semibold transition-colors text-sm">
              Call Now
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/30 rounded-full px-4 py-2 mb-6">
              <Clock className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 font-semibold text-sm">{businessInfo.availability}</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Expert Electrical Services for Your Home
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              IBEW certified electrician with {businessInfo.experience} experience serving the {businessInfo.serviceArea}. Professional, reliable, and always available when you need us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${businessInfo.phone}`}>
                <Button size="lg" className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-lg px-8 py-6 w-full sm:w-auto">
                  <Phone className="w-5 h-5 mr-2" />
                  {businessInfo.phone}
                </Button>
              </a>
              <Button size="lg" variant="outline" onClick={scrollToContact} className="border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black font-bold text-lg px-8 py-6 w-full sm:w-auto">
                Get Free Quote
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3">
              <div className="bg-yellow-400 p-3 rounded-lg">
                <Award className="w-6 h-6 text-black" />
              </div>
              <div>
                <p className="font-bold text-gray-900">IBEW Certified</p>
                <p className="text-sm text-gray-600">Licensed Professional</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3">
              <div className="bg-yellow-400 p-3 rounded-lg">
                <Shield className="w-6 h-6 text-black" />
              </div>
              <div>
                <p className="font-bold text-gray-900">{businessInfo.experience}</p>
                <p className="text-sm text-gray-600">Experience</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3">
              <div className="bg-yellow-400 p-3 rounded-lg">
                <Clock className="w-6 h-6 text-black" />
              </div>
              <div>
                <p className="font-bold text-gray-900">24/7 Available</p>
                <p className="text-sm text-gray-600">Emergency Service</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Comprehensive residential electrical solutions tailored to your needs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-2 border-gray-200 hover:border-yellow-400 transition-all duration-300 hover:shadow-xl group">
                  <CardHeader>
                    <div className="bg-yellow-400 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-black" strokeWidth={2.5} />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 text-base">{service.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-16 bg-gradient-to-r from-yellow-400 to-yellow-500">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 max-w-5xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="bg-black p-4 rounded-full">
                <Zap className="w-8 h-8 text-yellow-400" strokeWidth={2.5} />
              </div>
              <div>
                <h4 className="text-2xl font-bold text-black mb-1">24/7 Emergency Service</h4>
                <p className="text-black/80 text-lg">Electrical problems don't wait. Neither do we.</p>
              </div>
            </div>
            <a href={`tel:${businessInfo.phone}`}>
              <Button size="lg" className="bg-black hover:bg-gray-900 text-yellow-400 font-bold text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call Emergency Line
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold text-gray-900 mb-4">Why Choose AGK Electric?</h3>
              <p className="text-lg text-gray-600">Quality workmanship backed by years of experience</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 border-2 border-yellow-400">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment</h4>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    As an IBEW certified electrician with over 10 years of experience in the trade, we bring expertise and professionalism to every job. We specialize in residential electrical services throughout the Greater Toronto Area.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    From simple repairs to complex installations, we ensure every project meets the highest safety standards and exceeds your expectations.
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-yellow-400 p-2 rounded-lg mt-1">
                      <Shield className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Licensed & Insured</p>
                      <p className="text-sm text-gray-600">Full IBEW certification and comprehensive insurance coverage</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-yellow-400 p-2 rounded-lg mt-1">
                      <Award className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Quality Guaranteed</p>
                      <p className="text-sm text-gray-600">We stand behind our work with professional warranties</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-yellow-400 p-2 rounded-lg mt-1">
                      <MapPin className="w-5 h-5 text-black" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Serving the GTA</p>
                      <p className="text-sm text-gray-600">Fast, reliable service throughout the Greater Toronto Area</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Read reviews from our satisfied customers and share your experience</p>
          </div>

          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-gray-200 hover:border-yellow-400 transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Review Platforms CTA */}
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-yellow-400 bg-gradient-to-br from-yellow-50 to-white">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Share Your Experience</h4>
                  <p className="text-gray-600">Had a great experience with AGK Electric? Leave us a review on your favorite platform!</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {reviewPlatforms.map((platform, index) => {
                    const isGoogleDisabled = platform.name === "Google" && !businessInfo.social.google;
                    return (
                      <div key={index}>
                        {isGoogleDisabled ? (
                          <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-6 text-center opacity-60">
                            <Star className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                            <p className="font-bold text-gray-500 mb-1">{platform.name}</p>
                            <p className="text-sm text-gray-400 mb-3">{platform.description}</p>
                            <p className="text-xs text-gray-500 italic">Coming Soon</p>
                          </div>
                        ) : (
                          <a
                            href={platform.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`block ${platform.color} ${platform.hoverColor} text-white rounded-lg p-6 text-center transition-all duration-300 hover:shadow-lg transform hover:scale-105`}
                          >
                            {platform.name === "Facebook" && <Facebook className="w-10 h-10 mx-auto mb-3" />}
                            {platform.name === "Instagram" && <Instagram className="w-10 h-10 mx-auto mb-3" />}
                            {platform.name === "Google" && <Star className="w-10 h-10 mx-auto mb-3" />}
                            <p className="font-bold mb-1">{platform.name}</p>
                            <p className="text-sm opacity-90 mb-3">{platform.description}</p>
                            <div className="flex items-center justify-center gap-1 text-sm">
                              <span>Leave Review</span>
                              <ExternalLink className="w-4 h-4" />
                            </div>
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
                {!businessInfo.social.google && (
                  <p className="text-center text-sm text-gray-500 mt-6 italic">
                    💡 Google Business page link will be added once created
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Get In Touch</h3>
            <p className="text-lg text-gray-600">Ready to start your project? Contact us today for a free quote</p>
          </div>
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <Card className="border-2 border-gray-200">
              <CardHeader>
                <CardTitle>Send Us a Message</CardTitle>
                <CardDescription>Fill out the form and we'll get back to you shortly</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <Input
                      name="email"
                      type="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <Input
                      name="phone"
                      type="tel"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <Input
                      name="service"
                      placeholder="Service Needed (e.g., EV Charger Install)"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="border-gray-300"
                    />
                  </div>
                  <div>
                    <Textarea
                      name="message"
                      placeholder="Tell us about your project..."
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300 min-h-32"
                    />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold">
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="border-2 border-gray-200 hover:border-yellow-400 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-yellow-400 p-3 rounded-lg">
                      <Phone className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Call Us</p>
                      <a href={`tel:${businessInfo.phone}`} className="text-xl font-bold text-gray-900 hover:text-yellow-600 transition-colors">
                        {businessInfo.phone}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 hover:border-yellow-400 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-yellow-400 p-3 rounded-lg">
                      <Mail className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Email Us</p>
                      <a href={`mailto:${businessInfo.email}`} className="text-lg font-bold text-gray-900 hover:text-yellow-600 transition-colors break-all">
                        {businessInfo.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 hover:border-yellow-400 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-yellow-400 p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Service Area</p>
                      <p className="text-lg font-bold text-gray-900">{businessInfo.serviceArea}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-yellow-400 bg-yellow-50">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-700 mb-3">Follow Us</p>
                    <div className="flex justify-center gap-4">
                      <a href={businessInfo.social.facebook} target="_blank" rel="noopener noreferrer" className="bg-black hover:bg-gray-900 p-3 rounded-lg transition-colors">
                        <Facebook className="w-6 h-6 text-yellow-400" />
                      </a>
                      <a href={businessInfo.social.instagram} target="_blank" rel="noopener noreferrer" className="bg-black hover:bg-gray-900 p-3 rounded-lg transition-colors">
                        <Instagram className="w-6 h-6 text-yellow-400" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <img src="/agk-logo.jpg" alt="AGK Electric Inc. Logo" className="h-14 w-14 object-contain" />
                  <div>
                    <h5 className="text-lg font-bold">{businessInfo.name}</h5>
                    <p className="text-xs text-yellow-400">{businessInfo.certification}</p>
                  </div>
                </div>
                <p className="text-gray-400 text-sm">
                  Professional residential electrical services serving the Greater Toronto Area with {businessInfo.experience} of experience.
                </p>
              </div>
              <div>
                <h6 className="font-bold text-yellow-400 mb-4">Quick Links</h6>
                <ul className="space-y-2 text-sm">
                  <li><a href="#services" className="text-gray-400 hover:text-yellow-400 transition-colors">Services</a></li>
                  <li><a href="#about" className="text-gray-400 hover:text-yellow-400 transition-colors">About Us</a></li>
                  <li><a href="#testimonials" className="text-gray-400 hover:text-yellow-400 transition-colors">Customer Reviews</a></li>
                  <li><a href="#contact" className="text-gray-400 hover:text-yellow-400 transition-colors">Contact</a></li>
                  <li><a href={`tel:${businessInfo.phone}`} className="text-gray-400 hover:text-yellow-400 transition-colors">Emergency Service</a></li>
                </ul>
              </div>
              <div>
                <h6 className="font-bold text-yellow-400 mb-4">Contact</h6>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a href={`tel:${businessInfo.phone}`} className="hover:text-yellow-400 transition-colors">{businessInfo.phone}</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${businessInfo.email}`} className="hover:text-yellow-400 transition-colors break-all">{businessInfo.email}</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{businessInfo.serviceArea}</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} {businessInfo.name}. All rights reserved.
              </p>
              <div className="flex gap-4">
                <a href={businessInfo.social.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-yellow-400 transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href={businessInfo.social.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-yellow-400 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
